import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, signal, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import { IApplicationDTO } from "../../../core-ui/models/IApplication";
import { ConversationService } from "../../services/conversation.service";
import { combineLatestWith, filter } from "rxjs";
import {
  CUSTOMER_CALL_ACCEPTED_ACK,
  PARAM_CONVERSATION_ORIGIN,
  PARAM_CONVERSATION_CHANNEL,
} from "../../app.enums";
import { IConversation, IMember } from "@auvious/integrations";
import { AppointmentService } from "../../../core-ui/services/appointment.service";
import {
  IAppointment,
  IAppointmentRouting,
  IAppointmentRoutingGenesysCloudQueue,
  AppointmentParticipantStateEnum,
  AppointmentRoutingTypeEnum,
} from "../../../core-ui/models/Appointment";
import {
  IThemeOptions,
  ThemeOptions,
} from "../../../core-ui/models/application-options/ThemeOptions";
import { AppConfigService } from "../../../core-ui/services/app.config.service";
import {
  ConversationOriginEnum,
  DEFAULT_APPOINTMENT_CHAT_MODE,
  PARAM_TICKET_ID,
  PublicParam,
  UserRoleEnum,
  UserService,
} from "../../../core-ui";
import { GenericErrorHandler } from "../../services/error-handlers.service";
import { isDebugOn } from "../../../core-ui/services/utils";

@Component({
  selector: "app-appointment-page",
  templateUrl: "./appointment.component.html",
  styleUrls: ["./appointment.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppointmentPageComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private conversation = inject(ConversationService);
  private cd = inject(ChangeDetectorRef);
  private router = inject(Router);
  private appointmentService = inject(AppointmentService);
  private handler = inject(GenericErrorHandler);
  private user = inject(UserService);
  private destroyRef = inject(DestroyRef);

  options: IApplicationDTO;
  state = signal("idle");
  startAt: Date;
  appointment: IAppointment<IAppointmentRouting>;
  theme: IThemeOptions;
  timezone: string;
  isDebug = !!isDebugOn();
  isLoading = false;
  isError = false;
  errorMessage;
  queueName: string;

  constructor() {
    const config = inject(AppConfigService);

    this.theme = ThemeOptions.create(config.publicParam(PublicParam.THEME));
  }

  async ngOnInit() {
    this.appointmentService.appointmentChanged$
      .pipe(
        filter((a) => !!a),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((appointment) => {
        this.appointment = appointment;
        this.startAt = this.appointment.start;
        this.timezone = this.appointment.timezone;

        if (
          appointment.participants.find(
            (p) => p.id === this.user.appointmentParticipantId
          ).state == "UNSET"
        ) {
          this.appointmentService.updateParticipantState(
            this.appointment.id,
            AppointmentParticipantStateEnum.WAITING
          );
        }

        // fill in integration-specific data
        switch (this.appointment.interaction.routing.type) {
          case AppointmentRoutingTypeEnum.GENESYS_CLOUD_QUEUE:
            this.queueName = (
              this.appointment.interaction
                .routing as IAppointmentRoutingGenesysCloudQueue
            ).properties.queueName;
            break;
        }
      });

    this.conversation.connecting$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.state.set("connecting");
    });
    this.conversation.connected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.state.set("connected");
    });
    this.conversation.agentNotifified$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.state.set("agent_notified");
    });
    this.conversation.agentJoined$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((agent: IMember) => {
      this.state.set("agent_joined");
      this.conversation.setAgentJoined(this.appointment.id);
      // ngOnInit was not running in conferenceComponent unless we use ngZone
      this.goToRoom();
    });

    // on a page refresh, the agentJoined event is sent before the started event.
    // in that case, we don't have a conversation id to send the message to.
    // wait for both to email a value and send the ACK message
    this.conversation.agentJoined$
      .pipe(
        combineLatestWith(this.conversation.started$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((_) => {
        this.conversation.sendMessage(CUSTOMER_CALL_ACCEPTED_ACK, "notice");
      });

    this.conversation.started$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((conversation: IConversation) => {
      this.state.set("started");
      if (
        this.appointment.interaction.routing.type ===
          AppointmentRoutingTypeEnum.TALKDESK_DIGITAL_CONNECT &&
        this.conversation.isAgentAlreadyJoined(this.appointment.id)
      ) {
        // in digital connect, we do not get agent joined if we try to rejoin the conversation
        this.goToRoom();
      }
    });
    this.conversation.agentLeft$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.state.set("agent_left");
      this.cd.detectChanges();
    });
    this.conversation.customerLeft$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.state.set("customer_left");
    });
    this.conversation.ended$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_) => {
      this.state.set("ended");
      if (!this.isError) {
        // redirect
        this.router.navigate([
          "/thank-you",
          UserRoleEnum.customer.toLowerCase(),
        ]);
      }
    });

    this.conversation.error$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((e: any) => {
      this.state.set("error");
      // e.data means the error comes from genesys
      if (e.data || e.message) {
        this.isError = true;
        this.errorMessage =
          e.message || e.data?.errors?.[0]?.response?.responseJSON?.message;
      }
      this.handler.handleError(e);
      this.cd.detectChanges();
    });
  }

  private goToRoom() {
    this.router.navigate(
      ["/t", this.route.snapshot.paramMap.get(PARAM_TICKET_ID)],
      {
        queryParams: {
          [PARAM_CONVERSATION_ORIGIN]: ConversationOriginEnum.APPOINTMENT,
          [PARAM_CONVERSATION_CHANNEL]:
            this.conversation.getConversationChatMode(this.appointment),
        },
      }
    );
  }

  ngOnDestroy(): void {
    if (this.state() !== "agent_joined") {
      this.appointmentService.updateParticipantState(
        this.appointment.id,
        AppointmentParticipantStateEnum.LEFT
      );
    }
  }
}
